import React, { useState, useCallback, useMemo } from "react";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Link,
  IconButton,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { AUTH } from "src/platform/constants/routes";
import debugLogger from "src/platform/utils/debugLogger";

import { OAuthError } from "../types/OAuthError";

import GithubButton from "./GithubButton";
import GoogleButton from "./GoogleButton";

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const getPasswordStrength = (
  password: string,
): { score: number; label: string; color: string } => {
  if (!password) return { score: 0, label: "", color: "transparent" };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: "Weak", color: "error" };
  if (score <= 3) return { score: 2, label: "Medium", color: "warning" };
  return { score: 3, label: "Strong", color: "success" };
};

const SignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { signUp, loading, error, authInProgress } = useLegacyAuth();
  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<SignUpFormData>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToS, setAgreedToS] = useState(false);
  const [success, setSuccess] = useState(false);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
      transition: "all 150ms ease",
      "& fieldset": {
        borderColor: isDark ? theme.palette.grey[600] : theme.palette.grey[200],
      },
      "&:hover fieldset": {
        borderColor: isDark ? theme.palette.grey[500] : theme.palette.grey[300],
      },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused": {
        boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      "&.Mui-focused": { color: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": { color: theme.palette.text.primary },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.secondary,
    },
    "& .MuiFormHelperText-root": { color: theme.palette.text.secondary },
  } as const;

  const passwordStrength = useMemo(
    () => getPasswordStrength(formData.password),
    [formData.password],
  );
  const strengthColor =
    passwordStrength.color === "error"
      ? theme.palette.error.main
      : passwordStrength.color === "warning"
        ? theme.palette.warning.main
        : passwordStrength.color === "success"
          ? theme.palette.success.main
          : passwordStrength.color;

  const validateForm = (): boolean => {
    const errors: Partial<SignUpFormData> = {};
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange =
    (field: keyof SignUpFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!validateForm() || !agreedToS) return;

      try {
        await signUp({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
        });
        setSuccess(true);
        window.setTimeout(() => {
          void navigate(AUTH.LOGIN.path, {
            state: { message: "Account created successfully! Please sign in." },
          });
        }, 1500);
      } catch (err) {
        debugLogger.error("Sign up error:", err);
      }
    },
    [formData, signUp, navigate, agreedToS],
  );

  const handleOAuthSuccess = (user: Record<string, unknown>) => {
    debugLogger.debug("OAuth signup successful:", user);
  };

  const handleOAuthError = (err: OAuthError) => {
    debugLogger.error("OAuth signup error:", err);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {/* Heading */}
      <Typography
        fontWeight={700}
        sx={{ color: "text.primary", mb: 0.5, fontSize: "1.625rem" }}
      >
        Create your account
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Get started with Rusha
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "8px",
            py: 1.5,
            px: 2,
            fontSize: "13px",
            "& .MuiAlert-icon": { display: "none" },
          }}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label="First name"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              disabled={loading}
              required
              sx={inputSx}
            />
            <TextField
              fullWidth
              label="Last name"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              disabled={loading}
              required
              sx={inputSx}
            />
          </Box>

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange("email")}
            placeholder="you@company.com"
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={loading}
            required
            sx={inputSx}
          />

          <Box>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange("password")}
              error={!!formErrors.password}
              helperText={formErrors.password || "Must be 8+ characters"}
              disabled={loading}
              required
              inputProps={{ minLength: 12 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      tabIndex={-1}
                      size="small"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
            {formData.password && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
              >
                <Box sx={{ display: "flex", gap: 0.5, flex: 1 }}>
                  {[1, 2, 3].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        height: 3,
                        flex: 1,
                        borderRadius: 1.5,
                        bgcolor:
                          i <= passwordStrength.score
                            ? strengthColor
                            : isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)",
                        transition: "all 150ms ease",
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: strengthColor,
                    fontWeight: 500,
                    minWidth: 50,
                  }}
                >
                  {passwordStrength.label}
                </Typography>
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Confirm password"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange("confirmPassword")}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                    title={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToS}
              onChange={(e) => setAgreedToS(e.target.checked)}
              size="small"
              sx={{
                color: "text.secondary",
                "&.Mui-checked": { color: "primary.main" },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              I agree to the{" "}
              <Link
                href="#"
                sx={{
                  textDecoration: "none",
                  fontWeight: 500,
                  color: "primary.main",
                }}
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                href="#"
                sx={{
                  textDecoration: "none",
                  fontWeight: 500,
                  color: "primary.main",
                }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
          sx={{ mt: 2, mb: 2, alignItems: "flex-start" }}
        />

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={loading || authInProgress || success || !agreedToS}
          sx={{
            py: 1.5,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9375rem",
            ...(success
              ? {
                  color: "#ffffff",
                  background: theme.palette.success.main,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                }
              : {
                  color: theme.palette.background.default,
                  background:
                    "linear-gradient(90deg, #00d4ff 0%, #7b61ff 100%)",
                  boxShadow: "0 2px 12px rgba(0, 212, 255, 0.25)",
                }),
            transition: "all 150ms ease",
            "&:hover": success
              ? {
                  background: theme.palette.success.main,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                }
              : {
                  background:
                    "linear-gradient(90deg, #00d4ff 0%, #7b61ff 100%)",
                  boxShadow: "0 4px 16px rgba(0, 212, 255, 0.35)",
                },
            "&:active": { transform: "scale(0.98)" },
            "&:disabled": success
              ? {
                  bgcolor: theme.palette.success.main,
                  color: "#ffffff",
                  boxShadow: "0 2px 12px rgba(34, 197, 94, 0.25)",
                }
              : {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                  color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                },
            "&.Mui-focusVisible": {
              boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${theme.palette.primary.main}`,
            },
          }}
        >
          {success ? (
            "✓ Account created!"
          ) : loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={18} color="inherit" />
              <span>Creating account...</span>
            </Box>
          ) : (
            "Create account"
          )}
        </Button>

        <Box sx={{ position: "relative", my: 3 }}>
          <Divider sx={{ borderColor: "divider" }} />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.default",
              px: 2,
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            Or sign up with
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <GoogleButton
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            disabled={loading || authInProgress || success}
          />
          <GithubButton
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            disabled={loading || authInProgress || success}
          />
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Already have an account?{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate(AUTH.LOGIN.path)}
              sx={{
                textDecoration: "none",
                fontWeight: 600,
                color: "primary.main",
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </form>
    </Box>
  );
};

export default SignUpForm;
